/**
 * Окно просмотра договора, ТЗ и обоснования НМЦК карточки аукциона (UI-3). Native <dialog>
 * почти на весь экран с iframe на GET …/documents/{id}/view: HTML для docx/xlsx, встроенный
 * просмотрщик браузера для pdf, извлечённый текст для .doc. Та же origin, что и API на проде:
 * cookie сессии уходит, frame-ancestors 'self' пускает фрейм.
 *
 * ТЕМА-1: документ по умолчанию на белом фоне (…/view?theme=light), кнопка ☾/☀ в шапке
 * переключает на тёмный; выбор хранится в localStorage. Для pdf кнопки нет.
 *
 *   openDocViewer(tender, docId);
 */
import { api } from '../api.js';
import { esc, fmtBytes } from '../utils/tenders.js';
import { VIEW_GROUPS, VIEW_KIND_LABEL, fileFormat, viewGroupKey, viewGroups } from '../utils/doc-view.js';

let _dlg = null;
let _t = null;
let _doc = null;

const THEME_KEY = 'ccpm.docViewer.theme';
let _theme = loadTheme();

function loadTheme() {
  try { return localStorage.getItem(THEME_KEY) === 'dark' ? 'dark' : 'light'; } catch { return 'light'; }
}

function saveTheme() {
  try { localStorage.setItem(THEME_KEY, _theme); } catch { /* приватный режим — только на сессию */ }
}

export function openDocViewer(t, docId) {
  const groups = viewGroups(t);
  const doc = Object.values(groups).flat().find((d) => d.id === docId);
  if (!doc) return;
  _t = t;
  if (!_dlg) build();
  show(doc);
  if (!_dlg.open) _dlg.showModal();
}

export const closeDocViewer = () => { if (_dlg?.open) _dlg.close(); };

function build() {
  _dlg = document.createElement('dialog');
  _dlg.className = 'doc-viewer';
  _dlg.setAttribute('data-doc-viewer', '');
  _dlg.innerHTML = `
    <header class="dv-head">
      <div class="dv-title"><b data-dv-name></b><span class="dv-meta" data-dv-meta></span></div>
      <nav class="dv-switch" data-dv-switch></nav>
      <div class="dv-actions">
        <button type="button" class="ti-btn" data-dv-theme></button>
        <a class="ti-btn" data-dv-newtab target="_blank" rel="noopener">В новой вкладке</a>
        <a class="ti-btn" data-dv-download download>Скачать оригинал</a>
        <button type="button" class="dv-close" data-dv-close title="Закрыть (Esc)" aria-label="Закрыть">✕</button>
      </div>
    </header>
    <div class="dv-note" data-dv-note hidden></div>
    <iframe class="dv-frame" data-dv-frame title="Документ"></iframe>`;
  document.body.appendChild(_dlg);
  _dlg.addEventListener('click', onClick);
  _dlg.addEventListener('change', (e) => {
    if (e.target.matches('[data-dv-file]')) pick(Number(e.target.value));
  });
  _dlg.addEventListener('close', () => {
    // Выгрузить документ: pdf-просмотрщик и большие таблицы не держим в памяти.
    _dlg.querySelector('[data-dv-frame]').src = 'about:blank';
    _t = null;
    _doc = null;
  });
  // Esc, пока фокус внутри фрейма (HTML-страница той же origin; pdf-просмотрщик недоступен).
  _dlg.querySelector('[data-dv-frame]').addEventListener('load', (e) => {
    try {
      e.target.contentDocument?.addEventListener('keydown', (k) => { if (k.key === 'Escape') closeDocViewer(); });
    } catch { /* чужая origin — только кнопка и Esc вне фрейма */ }
  });
}

function onClick(e) {
  // Клик по затемнению: target — сам dialog, точка вне его прямоугольника.
  if (e.target === _dlg) {
    const r = _dlg.getBoundingClientRect();
    if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) _dlg.close();
    return;
  }
  const el = e.target instanceof Element ? e.target : null;
  if (el?.closest('[data-dv-close]')) { _dlg.close(); return; }
  if (el?.closest('button[data-dv-theme]')) {
    _theme = _theme === 'dark' ? 'light' : 'dark';
    saveTheme();
    applyTheme();
    return;
  }
  const g = el?.closest('[data-dv-group]');
  if (g && !g.disabled) pick(viewGroups(_t)[g.dataset.dvGroup][0]?.id);
}

function pick(docId) {
  const doc = Object.values(viewGroups(_t)).flat().find((d) => d.id === docId);
  if (doc && doc.id !== _doc?.id) show(doc);
}

function show(doc) {
  _doc = doc;
  const groups = viewGroups(_t);
  const cur = viewGroupKey(doc);
  const file = api.tenders.documentUrl(_t.id, doc.id, 'file');
  _dlg.querySelector('[data-dv-name]').textContent = doc.filename || `Документ ${doc.id}`;
  _dlg.querySelector('[data-dv-meta]').textContent =
    [VIEW_KIND_LABEL[doc.doc_kind], fileFormat(doc), fmtBytes(doc.file_size)].filter(Boolean).join(' · ');
  const same = groups[cur];
  _dlg.querySelector('[data-dv-switch]').innerHTML = VIEW_GROUPS.map((g) => {
    const n = groups[g.key].length;
    return `<button type="button" class="dv-tab${g.key === cur ? ' is-active' : ''}" data-dv-group="${g.key}"
      aria-pressed="${g.key === cur}"${n ? '' : ' disabled title="Нет файла"'}>${g.label}${n > 1 ? ` (${n})` : ''}</button>`;
  }).join('') + (same.length > 1
    ? `<select class="dv-file" data-dv-file aria-label="Файл">${same.map((d) =>
      `<option value="${d.id}"${d.id === doc.id ? ' selected' : ''}>${esc(d.filename || d.id)}</option>`).join('')}</select>`
    : '');
  _dlg.querySelector('[data-dv-download]').href = file;
  const note = _dlg.querySelector('[data-dv-note]');
  note.hidden = doc.view_kind !== 'text';
  note.innerHTML = doc.view_kind === 'text'
    ? `Извлечённый текст, форматирование не сохранено · оригинал: <a href="${esc(file)}" download data-dv-note-file>скачать</a>` : '';
  applyTheme();
}

/** Кнопка, фон фрейма, «В новой вкладке» и сам документ — по текущей теме. */
function applyTheme() {
  const dark = _theme === 'dark';
  const btn = _dlg.querySelector('button[data-dv-theme]');
  btn.hidden = _doc.view_kind === 'pdf';
  btn.textContent = dark ? '☀ Белый фон' : '☾ Тёмный фон';
  btn.title = dark ? 'Показать документ на белом фоне' : 'Показать документ на тёмном фоне';
  btn.setAttribute('aria-pressed', String(dark));
  _dlg.setAttribute('data-dv-theme', _theme);
  const view = `${api.tenders.documentUrl(_t.id, _doc.id, 'view')}?theme=${_theme}`;
  _dlg.querySelector('[data-dv-newtab]').href = view;
  _dlg.querySelector('[data-dv-frame]').src = view;
}
